// Sidebar "Get started" widget — compact card that surfaces the
// next undone onboarding step, anchored to a popover with the full
// list. Lives between the Messages nav item and the user-card at
// the sidebar's bottom so it uses dead vertical space without
// pushing nav items out of the visible area.
//
// State sources:
//   • Server: GET /api/trainer/onboarding-status returns 6 booleans
//     (one per checklist item). Fetched once on mount, cached in
//     sessionStorage so revisits across tabs don't re-fetch.
//   • localStorage: dismissal flag. Versioned key so we can revive
//     the widget when we add new steps in the future.
//
// Auto-behavior:
//   • Hidden entirely when all items complete + dismissed
//   • Collapses to a single pill (✦ 5/6) above 70% complete
//     so it stops feeling naggy in the home stretch
//   • Mobile (≤900px): hidden — the sidebar drawer is too cramped;
//     a dashboard tile is a better surface there (future).

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Popover } from 'antd';
import { Link, useLocation } from 'react-router-dom';
import { fetchAPI } from '../../ducks/api';
import { toast } from '../Toast';
import { IcoCheck, IcoX, IcoSparkle } from './icons';

const STATUS_CACHE_KEY = 'onboarding:status:v1';
const DISMISSED_KEY = 'onboarding:dismissed:v1';

const DEFAULT_STATUS = {
  hasBusinessIdentity: false,
  hasClients: false,
  hasMealplans: false,
  hasWorkouts: false,
  hasBroadcasts: false,
  hasCustomAssessment: false,
};

// Checklist items in display order. Each item maps a status-key
// bit to a label + the page where the trainer can act on it. The
// first item is the only one that auto-completes from existing
// data (most trainers will land with it pre-checked).
const ITEMS = [
  {
    key: 'hasBusinessIdentity',
    label: 'Set business identity',
    href: '/dashboard/settings/business',
  },
  {
    key: 'hasClients',
    label: 'Invite your first client',
    // ?invite=1 opens the invite form (same as Create → New client).
    href: '/dashboard/admin/clients?invite=1',
  },
  {
    key: 'hasMealplans',
    label: 'Create your first meal plan template',
    href: '/dashboard/admin/library/meal-plan-v2',
  },
  {
    key: 'hasWorkouts',
    label: 'Create your first workout template',
    href: '/dashboard/admin/library/workout',
  },
  {
    key: 'hasBroadcasts',
    label: 'Send your first broadcast',
    // `compose=1` auto-opens the BroadcastModal on the messages
    // page so the trainer lands directly in the compose flow
    // instead of having to find the Broadcast button. The page
    // strips the param after opening so a refresh doesn't keep
    // re-firing the modal.
    href: '/dashboard/messages?tab=broadcasts&compose=1',
  },
  {
    key: 'hasCustomAssessment',
    label: 'Create a custom assessment',
    href: '/dashboard/admin/assessments',
  },
];

function readCachedStatus() {
  try {
    const raw = sessionStorage.getItem(STATUS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_STATUS, ...parsed };
  } catch (_) {
    return null;
  }
}

function writeCachedStatus(next) {
  try {
    sessionStorage.setItem(STATUS_CACHE_KEY, JSON.stringify(next));
  } catch (_) {
    // private-browsing → no-op; component still works, just won't
    // cross-page-survive a refresh.
  }
}

function readDismissed() {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch (_) {
    // no-op
  }
}

function clearDismissed() {
  try {
    localStorage.removeItem(DISMISSED_KEY);
  } catch (_) {
    // no-op
  }
}

export default function OnboardingChecklist() {
  const [status, setStatus] = useState(() => readCachedStatus() || DEFAULT_STATUS);
  const [dismissed, setDismissed] = useState(() => readDismissed());
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Fetch on mount and again as the trainer moves around the studio, so a
  // step ticks off soon after they do it (the sidebar stays mounted, so a
  // once-per-mount fetch only refreshed on a full reload). At most every 15s,
  // and not once everything is done. Errors keep the last known status.
  const { pathname } = useLocation();
  const lastFetchRef = useRef(0);
  const allDoneRef = useRef(false);
  useEffect(() => {
    if (allDoneRef.current) return undefined;
    if (Date.now() - lastFetchRef.current < 15000) return undefined;
    lastFetchRef.current = Date.now();
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchAPI('/trainer/onboarding-status');
        if (cancelled || !data) return;
        const merged = { ...DEFAULT_STATUS, ...data };
        setStatus(merged);
        writeCachedStatus(merged);
      } catch (_) {
        // Keep whatever we had from the cache; widget stays usable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  const { completedCount, percent, nextItem, allDone } = useMemo(() => {
    const done = ITEMS.filter((it) => status[it.key]).length;
    const total = ITEMS.length;
    const next = ITEMS.find((it) => !status[it.key]) || null;
    return {
      completedCount: done,
      percent: total ? Math.round((done / total) * 100) : 0,
      nextItem: next,
      allDone: done === total,
    };
  }, [status]);
  allDoneRef.current = allDone; // stop refetching once every step is done

  const handleDismiss = useCallback((e) => {
    e?.stopPropagation();
    // Hide immediately for a snappy feel; commit to localStorage
    // right away so a hard refresh respects the dismissal. Undo
    // reverses both — clears the flag and brings the widget back.
    setDismissed(true);
    writeDismissed();
    setPopoverOpen(false);
    toast.info('Onboarding hidden', {
      duration: 6000,
      action: {
        label: 'Undo',
        onClick: () => {
          clearDismissed();
          setDismissed(false);
        },
      },
    });
  }, []);

  if (dismissed) return null;
  // Once everything's done, auto-hide — the persistent dismissal
  // flag stays unset so adding new steps in the future re-shows
  // the widget naturally.
  if (allDone) return null;

  // Collapsed "pill" mode above 70% complete — same component,
  // smaller surface, less naggy as the trainer wraps up.
  const isCollapsed = percent >= 70 && !popoverOpen;

  return (
    <Popover
      placement="rightTop"
      trigger="click"
      visible={popoverOpen}
      onVisibleChange={setPopoverOpen}
      overlayClassName="onb-popover-overlay"
      content={
        <OnboardingPopoverContent
          status={status}
          onDismiss={handleDismiss}
          onItemClick={() => setPopoverOpen(false)}
        />
      }
    >
      {isCollapsed ? (
        <button
          type="button"
          className="onb-pill"
          aria-label={`Get started — ${completedCount} of ${ITEMS.length} complete`}
        >
          <IcoSparkle size={12} />
          <span>{completedCount}/{ITEMS.length}</span>
        </button>
      ) : (
        <button
          type="button"
          className="onb-card"
          aria-label="Get started checklist"
        >
          <div className="onb-card-head">
            <span className="onb-card-title">
              <IcoSparkle size={12} /> Get started
            </span>
            <span
              className="onb-card-dismiss"
              role="button"
              tabIndex={0}
              aria-label="Dismiss onboarding"
              onClick={handleDismiss}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleDismiss(e);
                }
              }}
            >
              <IcoX size={11} />
            </span>
          </div>
          <div className="onb-card-progress">
            <div
              className="onb-card-progress-fill"
              style={{ width: `${percent}%` }}
            />
          </div>
          <div className="onb-card-meta">
            <span className="onb-card-count">
              {completedCount}/{ITEMS.length}
            </span>
            {nextItem ? (
              <span className="onb-card-next">Next: {nextItem.label}</span>
            ) : null}
          </div>
        </button>
      )}
    </Popover>
  );
}

function OnboardingPopoverContent({ status, onDismiss, onItemClick }) {
  return (
    <div className="onb-popover">
      <div className="onb-popover-head">
        <div>
          <div className="onb-popover-title">Get started</div>
          <div className="onb-popover-sub">
            A short setup so your dashboard isn't empty.
          </div>
        </div>
        <button
          type="button"
          className="onb-popover-close"
          onClick={onItemClick}
          aria-label="Close"
          title="Close"
        >
          <IcoX size={14} />
        </button>
      </div>
      <ul className="onb-popover-list">
        {ITEMS.map((item) => {
          const done = !!status[item.key];
          return (
            <li
              key={item.key}
              className={`onb-popover-item ${done ? 'is-done' : ''}`}
            >
              <span className="onb-popover-check">
                {done ? <IcoCheck size={12} /> : null}
              </span>
              {done ? (
                <span className="onb-popover-label">{item.label}</span>
              ) : (
                <Link
                  to={item.href}
                  className="onb-popover-label onb-popover-link"
                  onClick={onItemClick}
                >
                  {item.label}
                </Link>
              )}
            </li>
          );
        })}
      </ul>
      <button
        type="button"
        className="onb-popover-dontshow"
        onClick={onDismiss}
      >
        Don't show anymore
      </button>
    </div>
  );
}
